import { resolveTikTokAdvertiserId } from '../utils/tiktokAdvertiserId';
import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes, faFile, faTrash } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import './UploadCreativesModal.css';

const UploadCreativesModal = ({ isOpen, onClose, campaign, orchestrationStatus, campaignMessages, accountId, user, onUploadComplete, accountSettings }) => {
  const [files, setFiles] = useState([]);
  const [subAction, setSubAction] = useState('display-video');
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [pageId, setPageId] = useState('');
  // Facebook page selector — populated on modal open via /api/v2/facebook/pages.
  // When pages load successfully, we show a dropdown; fallback to free-text if
  // the endpoint errors (no FB channel, token expired, etc.).
  const [fbPages, setFbPages] = useState([]);
  const [fbPagesError, setFbPagesError] = useState(null);
  const [fbPagesLoaded, setFbPagesLoaded] = useState(false);
  const [pageIdManualEntry, setPageIdManualEntry] = useState(false);
  // Instagram account selector — fetched when pageId changes.
  const [instagramUserId, setInstagramUserId] = useState('');
  const [igAccounts, setIgAccounts] = useState([]);
  const [igAccountsLoading, setIgAccountsLoading] = useState(false);
  const [igAccountsError, setIgAccountsError] = useState(null);
  const [creativeSource, setCreativeSource] = useState('upload'); // 'upload' or 'fetch'
  const [mmgyMappingFile, setMmgyMappingFile] = useState(null);
  const [customInstruction, setCustomInstruction] = useState('');
  const [adPrefix, setAdPrefix] = useState('');
  const [adSuffix, setAdSuffix] = useState('');
  const [includeDate, setIncludeDate] = useState(false);
  const [mediaBrief, setMediaBrief] = useState('');
  // Dry-run mode (boolean) — both DV360 and TTD: upload creatives, preview the
  // planned assignments, skip the assign step. User can approve & apply from
  // the dry-run review UI.
  const [dryRun, setDryRun] = useState(false);
  const [creativeNameFilter, setCreativeNameFilter] = useState('');
  const [adName, setAdName] = useState('');
  const [selectedAdSetIds, setSelectedAdSetIds] = useState([]);
  const [adSetSelectAll, setAdSetSelectAll] = useState(false);
  const fileInputRef = useRef(null);
  const mmgyFileInputRef = useRef(null);
  const { accounts, currentAccountName } = useAccount();

  // Check if this is an MMGY account (mirrors backend mmgyUtils.js logic).
  // Prefer currentAccountName, which AccountContext populates for every role.
  // Fall back to the accounts[] lookup for admin/super-admin paths where
  // currentAccountName may not be populated yet. Without the fallback,
  // campaign managers — who never get accounts[] populated (AccountContext
  // skips fetch for non-regular-admins) — would see isMMGYAccount === false
  // and lose the MMGY mapping file input.
  const accountNameFromList = accounts?.find(a => String(a.id) === String(accountId))?.name || '';
  const resolvedAccountName = currentAccountName || accountNameFromList || '';
  const isMMGYAccount = resolvedAccountName
    && (resolvedAccountName.toLowerCase() === 'mmgy' || resolvedAccountName.toLowerCase().startsWith('mmgy-'));

  // PMax assign-creatives asks for a logo URL rather than a landing page URL:
  // the PMax creative workbook already carries the final URL in its campaign
  // settings, whereas its Logo rows are routinely placeholders. Without an
  // explicit logo each asset group silently falls back to its smallest square
  // image, which Google Ads accepts as a 1:1 logo — so a wrong logo ships with
  // only a warning. Both destination spellings occur in practice.
  const isPmaxCampaign = ['google-ads-pmax', 'google_pmax'].includes(
    (campaign?.destination || '').toLowerCase()
  );

  // Whether the Landing Page URL field is required for this account.
  // Default: required (preserves prior behavior — every account requires it today).
  // Override: account setting `optional-landing-page-url-assign-creative` = 'true'
  // makes it optional. Positive opt-in by design — only the small set of accounts
  // that don't want this gate need the flag set; everyone else is unaffected.
  const isLandingPageUrlRequired =
    accountSettings?.['optional-landing-page-url-assign-creative'] !== 'true';

  // PMax additionally requires a logo URL. It does not replace the landing page
  // URL: only the IDFC-shaped PMax workbook carries a final_url of its own, so
  // for every other PMax workbook the landing page URL is still the launcher's
  // only source of a campaign-level final URL.
  const isLogoUrlRequired = isPmaxCampaign;

  // Extract available ad sets for Facebook campaigns (mirrors EditCampaignModal pattern)
  const getAvailableAdSets = () => {
    if (campaign?.destination !== 'facebook') return [];

    const apiResult = orchestrationStatus?.apiResult;
    const details = apiResult?.details;
    const platformCampaignId = orchestrationStatus?.apiResult?.details?.campaign?.id ||
      orchestrationStatus?.apiResult?.campaign_id || campaign?.platform_campaign_id;

    // Priority 1: refresh-campaign messages
    const refreshMessages = campaignMessages?.filter(msg => {
      const isRefresh = msg.action === 'refresh-campaign';
      const isCompleted = msg.status === 'completed';
      const jsonResp = msg.json_response;
      const hasSuccess = jsonResp?.success === true;
      return isRefresh && isCompleted && hasSuccess;
    }) || [];

    if (refreshMessages.length > 0) {
      const latest = refreshMessages[refreshMessages.length - 1];
      const adsets = latest.json_response?.details?.campaign?.adsets || [];
      if (adsets.length > 0) {
        return adsets.map(as => ({ id: as.id, name: as.name || `Ad Set ${as.id}` }));
      }
    }

    // Priority 2: orchestration result
    let adSets = details?.ad_sets || apiResult?.ad_sets ||
                 apiResult?.successful_ad_sets || details?.successful_ad_sets || [];

    // Priority 3: import-campaign messages
    if (adSets.length === 0 && campaignMessages) {
      const importMessages = campaignMessages.filter(msg => {
        const isImport = msg.action === 'import-campaign';
        const isCompleted = msg.status === 'completed';
        const jsonResp = msg.json_response;
        const hasSuccess = jsonResp?.api_result?.success === true || jsonResp?.success === true;
        const msgPlatform = jsonResp?.platform || jsonResp?.api_result?.platform || '';
        return isImport && isCompleted && hasSuccess && (msgPlatform.includes('facebook') || msgPlatform === 'fb');
      });

      if (importMessages.length > 0) {
        const importResult = importMessages[importMessages.length - 1]?.json_response?.api_result ||
                            importMessages[importMessages.length - 1]?.json_response;
        const campaigns = importResult?.campaigns || [];
        const importedCampaign = campaigns.find(c => String(c.campaign_id) === String(platformCampaignId)) || campaigns[0];
        if (importedCampaign?.ad_sets) {
          adSets = importedCampaign.ad_sets;
        }
      }
    }

    return adSets.map(as => ({
      id: as.ad_set_id || as.id,
      name: as.ad_set_name || as.name || `Ad Set ${as.ad_set_id || as.id}`
    }));
  };

  const availableAdSets = isOpen ? getAvailableAdSets() : [];

  // Reset all states when modal opens
  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setSubAction('display-video');
      setIsUploading(false);
      setDragActive(false);
      setLandingPageUrl('');
      setLogoUrl('');
      setPageId('');
      setFbPages([]);
      setFbPagesError(null);
      setFbPagesLoaded(false);
      setPageIdManualEntry(false);
      setInstagramUserId('');
      setIgAccounts([]);
      setIgAccountsLoading(false);
      setIgAccountsError(null);
      setCreativeSource('upload');
      setMmgyMappingFile(null);
      setCustomInstruction('');
      setAdPrefix('');
      setAdSuffix('');
      setIncludeDate(false);
      setMediaBrief('');
      setDryRun(false);
      setCreativeNameFilter('');
      setAdName('');
      setSelectedAdSetIds([]);
      setAdSetSelectAll(false);
      // Clear file inputs
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (mmgyFileInputRef.current) {
        mmgyFileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  // Fetch the account's Facebook Pages once the modal opens for a Facebook
  // campaign. We use the result to populate a page-selector dropdown; on
  // failure we fall back to the free-text page ID input (original UX).
  useEffect(() => {
    if (!isOpen) return;
    if (campaign?.destination !== 'facebook') return;
    if (!accountId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(getApiUrl('/api/v2/facebook/pages'), {
          // campaignId scopes the lookup to the FB channel the campaign was
          // launched against (campaigns.channel_id), so accounts with
          // multiple FB channels use the right token instead of the first
          // one found on the account.
          params: { accountId, campaignId: campaign?.id, userId: user?.userId || user?.id }
        });
        if (cancelled) return;
        const pages = Array.isArray(res.data?.pages) ? res.data.pages : [];
        setFbPages(pages);
        setFbPagesError(null);
        setFbPagesLoaded(true);
        // If the campaign already has a page ID saved, preselect it when
        // present in the list; otherwise the user picks manually.
        const preselect = campaign?.secondary_platform_id || '';
        if (preselect && pages.some(p => p.id === preselect)) {
          setPageId(preselect);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err.response?.data?.error || err.message || 'Failed to load Facebook pages';
        console.warn('[UploadCreativesModal] Failed to load FB pages, falling back to manual entry:', msg);
        setFbPagesError(msg);
        setFbPagesLoaded(true);
        setPageIdManualEntry(true); // show the free-text input as fallback
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, campaign?.id, campaign?.destination, campaign?.secondary_platform_id, accountId, user?.userId, user?.id]);

  // Fetch connected Instagram accounts whenever the selected Facebook Page
  // changes. Auto-selects the only IG account if there's exactly one; leaves
  // the user to choose if there are multiple. Shows a helper message if the
  // page has no connected IG account — the launcher will still run (IG is
  // optional for non-Reels assignments).
  useEffect(() => {
    if (!isOpen) return;
    if (campaign?.destination !== 'facebook') return;
    if (!pageId) {
      setIgAccounts([]);
      setInstagramUserId('');
      setIgAccountsError(null);
      return;
    }

    let cancelled = false;
    setIgAccountsLoading(true);
    setIgAccountsError(null);
    (async () => {
      try {
        const res = await api.get(
          getApiUrl(`/api/v2/facebook/pages/${encodeURIComponent(pageId)}/instagram-accounts`),
          { params: { accountId, campaignId: campaign?.id, userId: user?.userId || user?.id } }
        );
        if (cancelled) return;
        const accounts = Array.isArray(res.data?.accounts) ? res.data.accounts : [];
        setIgAccounts(accounts);
        // Auto-select when exactly one is connected; otherwise clear so the
        // user has to make an explicit choice.
        setInstagramUserId(accounts.length === 1 ? accounts[0].id : '');
      } catch (err) {
        if (cancelled) return;
        const msg = err.response?.data?.error || err.message || 'Failed to load Instagram accounts';
        console.warn('[UploadCreativesModal] Failed to load IG accounts for page:', msg);
        setIgAccounts([]);
        setInstagramUserId('');
        setIgAccountsError(msg);
      } finally {
        if (!cancelled) setIgAccountsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, campaign?.id, campaign?.destination, pageId, accountId, user?.userId, user?.id]);

  // Check if fetch from platform is enabled for this platform
  const isFetchFromPlatformEnabled = () => {
    const fetchPlatforms = accountSettings?.['fetch-creatives-during-assign'] || '';
    const platformList = fetchPlatforms.split(',').map(p => p.trim().toLowerCase());
    return platformList.includes(campaign?.destination?.toLowerCase());
  };

  if (!isOpen) return null;

  // Extract campaign and advertiser IDs from orchestration status (same data frontend displays)
  const extractPlatformIds = () => {
    console.log('🔍 extractPlatformIds() called for campaign:', campaign?.id, 'destination:', campaign?.destination);
    console.log('🔍 Frontend extraction debug:', {
      hasOrchestrationStatus: !!orchestrationStatus,
      orchestrationStatus: orchestrationStatus,
      campaignDestination: campaign.destination
    });

    const apiResult = orchestrationStatus?.apiResult;
    const details = apiResult?.details; // URLs are nested under details!

    console.log('🔍 API result debug:', {
      hasApiResult: !!apiResult,
      hasDetails: !!details,
      apiResultKeys: apiResult ? Object.keys(apiResult) : [],
      detailsKeys: details ? Object.keys(details) : [],
      // Check for Facebook URL in both locations
      fbUrlInDetails: details?.fb_url,
      fbUrlInApiResult: apiResult?.fb_url,
      // Full apiResult for debugging
      fullApiResult: apiResult
    });

    if (campaign.destination === 'tiktok') {
      return {
        platformCampaignId: campaign.platform_campaign_id || details?.campaign_id || apiResult?.campaign_id,
        advertiserId: resolveTikTokAdvertiserId(campaign, apiResult),
      };
    }

    // For some platforms, URLs might be directly in apiResult instead of nested under details
    if (!details && !apiResult) {
      console.log('❌ No details or apiResult found in orchestrationStatus');
      return null;
    }

    if (campaign.destination === 'dv360' && details?.dv360_url) {
      console.log('🔍 DV360 URL found:', details.dv360_url);
      // DV360 URL format: https://displayvideo.google.com/ng_nav/p/{partner_id}/a/{advertiser_id}/c/{campaign_id}/explorer
      const urlMatch = details.dv360_url.match(/\/a\/(\d+)\/c\/(\d+)/);
      if (urlMatch) {
        const result = {
          advertiserId: urlMatch[1],
          platformCampaignId: urlMatch[2], // DV360 numeric campaign ID
          platformUrl: details.dv360_url
        };
        console.log('✅ DV360 IDs extracted:', result);
        return result;
      } else {
        console.log('❌ DV360 URL regex match failed');
      }
    } else if (campaign.destination === 'ttd' && (details?.sandbox_url || details?.sandbox_urls)) {
      // Collect every sandbox URL the orchestration produced — an IO can yield
      // multiple TTD campaigns and we need to send all of their IDs back to
      // the launcher so creatives are assigned across all of them.
      const allSandboxUrls = Array.isArray(details.sandbox_urls) && details.sandbox_urls.length > 0
        ? details.sandbox_urls
        : (details.sandbox_url ? [details.sandbox_url] : []);
      console.log('🔍 TTD URLs found:', allSandboxUrls);
      // TTD URL format: https://ext-desk.sb.thetradedesk.com/app/home/campaign/{campaign_id}/optimizations
      const campaignIds = allSandboxUrls
        .map(url => url?.match(/\/campaign\/([^\/]+)/))
        .filter(Boolean)
        .map(match => match[1]);
      if (campaignIds.length > 0) {
        const result = {
          advertiserId: campaign.advertiser_id, // Use from campaign record
          platformCampaignId: campaignIds[0], // First ID for backward compatibility
          platformUrl: allSandboxUrls[0]
        };
        if (campaignIds.length > 1) {
          result.platformCampaignIds = campaignIds;
        }
        console.log('✅ TTD IDs extracted:', result);
        return result;
      } else {
        console.log('❌ TTD URL regex match failed');
      }
    } else if (campaign.destination === 'facebook') {
      // Check for Facebook URL in both locations: details.fb_url and apiResult.fb_url
      const fbUrl = details?.fb_url || apiResult?.fb_url;
      if (fbUrl) {
        console.log('🔍 Facebook URL found:', fbUrl);
        // Facebook URL format: https://adsmanager.facebook.com/adsmanager/manage/adsets?act=818484103806783&business_id=1561323971445600&selected_campaign_ids=120233035481550096&breakdown_regrouping=true
        const campaignIdMatch = fbUrl.match(/selected_campaign_ids=(\d+)/);
        const adAccountIdMatch = fbUrl.match(/[?&]act=(\d+)/);

        if (campaignIdMatch) {
          const result = {
            // Extract ad_account_id from URL (act parameter) or fall back to campaign record
            advertiserId: adAccountIdMatch ? adAccountIdMatch[1] : campaign.advertiser_id,
            platformCampaignId: campaignIdMatch[1], // Facebook numeric campaign ID
            platformUrl: fbUrl
          };
          console.log('✅ Facebook IDs extracted:', result);
          return result;
        } else {
          console.log('❌ Facebook URL regex match failed');
        }
      } else {
        console.log('❌ No Facebook URL found in details.fb_url or apiResult.fb_url');
      }
    } else if (campaign.destination === 'reddit' && details?.reddit_url) {
      console.log('🔍 Reddit URL found:', details.reddit_url);
      // Reddit URL format: https://ads.reddit.com/account/{account_id}/dashboard?entity=adgroups&source=tab&selectedIds=campaigns%3A{id1}%2Ccampaigns%3A{id2}
      const urlMatch = details.reddit_url.match(/selectedIds=([^&]+)/);
      if (urlMatch) {
        const selectedIds = decodeURIComponent(urlMatch[1]);
        // Extract all campaign IDs from selectedIds (format: "campaigns:id1,campaigns:id2")
        const campaignMatches = selectedIds.match(/campaigns:([^,]+)/g);
        if (campaignMatches && campaignMatches.length > 0) {
          // Extract campaign IDs from matches
          const campaignIds = campaignMatches.map(match => match.replace('campaigns:', ''));

          const result = {
            advertiserId: campaign.advertiser_id, // Use from campaign record
            platformCampaignId: campaignIds[0], // First campaign ID for backward compatibility
            platformUrl: details.reddit_url
          };

          // Add multiple campaign IDs if there are more than one
          if (campaignIds.length > 1) {
            result.platformCampaignIds = campaignIds; // Multiple IDs array for Reddit
          }

          console.log('✅ Reddit IDs extracted:', result);
          return result;
        } else {
          console.log('❌ Reddit campaign ID extraction failed from selectedIds:', selectedIds);
        }
      } else {
        console.log('❌ Reddit URL regex match failed for URL:', details.reddit_url);
      }
    } else if (campaign.destination === 'google-ads-search' || campaign.destination === 'google-ads-pmax' || campaign.destination === 'google-ads-demandgen') {
      console.log('🔍 Google Ads campaign detected:', campaign.destination);

      // Check if we have campaigns array from the API result (new format)
      if (orchestrationStatus?.apiResult?.campaigns && Array.isArray(orchestrationStatus.apiResult.campaigns)) {
        const campaigns = orchestrationStatus.apiResult.campaigns;
        console.log('🔍 Found campaigns array in apiResult:', campaigns);

        // Extract campaign IDs and URLs from campaigns array
        const campaignData = campaigns
          .filter(campaign => campaign.campaign_id && campaign.campaign_url)
          .map(campaign => ({
            id: campaign.campaign_id,
            url: campaign.campaign_url
          }));

        if (campaignData.length > 0) {
          const campaignIds = campaignData.map(c => c.id);
          const campaignUrls = campaignData.map(c => c.url);

          const result = {
            platformCampaignId: campaignIds[0], // Single ID for backward compatibility
            platformUrl: campaignUrls[0] // First URL for backward compatibility
          };

          // Add multiple campaign IDs for Google Search (to avoid regressions for other platforms)
          if (campaignIds.length > 1) {
            result.platformCampaignIds = campaignIds; // Multiple IDs array for Google Search
          }

          console.log('✅ Google Ads IDs extracted from campaigns array:', result);
          return result;
        }
      }
      // Fallback: Check if we have campaign_urls from the API result (legacy format)
      if (orchestrationStatus?.apiResult?.campaign_urls && Array.isArray(orchestrationStatus.apiResult.campaign_urls)) {
        const campaignUrls = orchestrationStatus.apiResult.campaign_urls;
        console.log('🔍 Found campaign URLs in apiResult:', campaignUrls);

        // Extract campaign IDs from URLs: https://ads.google.com/aw/campaigns?campaignId=12345
        const campaignIds = campaignUrls.map(url => {
          const match = url.match(/campaignId=([^&]+)/);
          return match ? match[1] : null;
        }).filter(Boolean);

        if (campaignIds.length > 0) {
          const result = {
            platformCampaignId: campaignIds[0], // Single ID for backward compatibility
            platformUrl: campaignUrls[0] // First URL for backward compatibility
          };

          // Add multiple campaign IDs for Google Ads (to avoid regressions for other platforms)
          if (campaignIds.length > 1) {
            result.platformCampaignIds = campaignIds; // Multiple IDs array for Google Ads
          }

          console.log('✅ Google Ads campaign IDs extracted from URLs:', result);
          return result;
        }
      }
    } else if (campaign.destination === 'madhive') {
      console.log('🔍 Madhive campaign detected');

      // Madhive campaign ID is available in multiple locations:
      // 1. details.campaign_id (direct field)
      // 2. details.campaign_results.response.id
      // 3. details.processed_config.lineItems[].campaignId
      const campaignId = details?.campaign_id ||
                        details?.campaign_results?.response?.id ||
                        details?.processed_config?.lineItems?.[0]?.campaignId;

      if (campaignId) {
        // Extract advertiser ID from campaign results
        const advertiserId = details?.campaign_results?.response?.advertiserId ||
                            details?.processed_config?.campaign?.advertiserId ||
                            campaign.advertiser_id;

        const result = {
          advertiserId: advertiserId,
          platformCampaignId: campaignId, // Madhive alphanumeric campaign ID
          platformUrl: null // Madhive doesn't provide a direct URL in the response
        };

        console.log('✅ Madhive IDs extracted:', result);
        return result;
      } else {
        console.log('❌ Madhive campaign ID not found in response');
      }
    } else if (campaign.destination === 'openai') {
      console.log('🔍 OpenAI campaign detected');

      // OpenAI campaign ID lives in details.campaign_results[].details.id
      // (also mirrored in details.campaign_id_to_name keys). There may be multiple
      // campaigns from a single launch — collect them all and use the first as the
      // backward-compatible single ID.
      const campaignResults = details?.campaign_results;
      let campaignIds = [];

      if (Array.isArray(campaignResults)) {
        campaignIds = campaignResults
          .filter(cr => cr?.success !== false && cr?.details?.id)
          .map(cr => cr.details.id);
      }

      // Fallback to campaign_id_to_name keys if campaign_results didn't yield IDs
      if (campaignIds.length === 0 && details?.campaign_id_to_name && typeof details.campaign_id_to_name === 'object') {
        campaignIds = Object.keys(details.campaign_id_to_name);
      }

      if (campaignIds.length > 0) {
        const result = {
          platformCampaignId: campaignIds[0], // OpenAI campaign ID (e.g. "cmpn_...")
          platformUrl: null // OpenAI Ads UI URL pattern not yet wired up
        };

        if (campaignIds.length > 1) {
          result.platformCampaignIds = campaignIds;
        }

        console.log('✅ OpenAI IDs extracted:', result);
        return result;
      } else {
        console.log('❌ OpenAI campaign ID not found in details.campaign_results or campaign_id_to_name');
      }
    } else if (campaign.destination === 'youtube-dv360') {
      console.log('🔍 YouTube DV360 campaign detected');
      console.log('🔍 YouTube DV360 debug:', {
        hasApiResult: !!apiResult,
        apiResultKeys: apiResult ? Object.keys(apiResult) : [],
        campaignId: apiResult?.campaign_id,
        fullApiResult: apiResult
      });

      // YouTube DV360 campaign ID is directly in apiResult.campaign_id
      const campaignId = apiResult?.campaign_id;
      const advertiserId = campaign.advertiser_id; // Use from campaign record

      if (campaignId) {
        const result = {
          advertiserId: advertiserId,
          platformCampaignId: campaignId, // YouTube DV360 numeric campaign ID
          platformUrl: null // YouTube DV360 doesn't provide a direct campaign URL
        };

        console.log('✅ YouTube DV360 IDs extracted:', result);
        return result;
      } else {
        console.log('❌ YouTube DV360 campaign ID not found in apiResult', {
          apiResult: apiResult,
          campaignAdvertiserId: campaign.advertiser_id
        });
      }
    } else {
      console.log('❌ No matching platform URL found:', {
        destination: campaign.destination,
        hasDv360Url: !!details?.dv360_url,
        hasSandboxUrl: !!details?.sandbox_url,
        hasSandboxUrls: !!(details?.sandbox_urls && details.sandbox_urls.length > 0),
        hasFbUrl: !!details?.fb_url,
        hasFbUrlInApiResult: !!apiResult?.fb_url,
        hasRedditUrl: !!details?.reddit_url,
        hasGoogleAdsUrl: !!details?.google_ads_url,
        hasMadhiveCampaignId: !!details?.campaign_id,
        hasYoutubeDv360CampaignId: !!apiResult?.campaign_id
      });
    }
    return null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Check if orchestration is in progress
  const isOrchestrationInProgress = orchestrationStatus?.status === 'processing';

  const handleUpload = async () => {
    // For fetch mode, files are not required
    const isFetchMode = creativeSource === 'fetch' && isFetchFromPlatformEnabled();
    if (!isFetchMode && files.length === 0) return;
    if (isOrchestrationInProgress) return;

    // Landing page URL — required by default; account setting
    // `optional-landing-page-url-assign-creative` = 'true' makes it optional.
    if (isLandingPageUrlRequired && !landingPageUrl.trim()) {
      alert('Landing Page URL is required to upload creative assets');
      return;
    }

    // PMax: the logo URL takes the landing page URL's place as the required field
    if (isLogoUrlRequired && !logoUrl.trim()) {
      alert('Logo URL is required to upload PMax creative assets');
      return;
    }

    // Facebook Page is required for Facebook campaigns
    if (campaign.destination === 'facebook' && !pageId.trim()) {
      alert('Facebook Page is required to upload creative assets');
      return;
    }
    // MMGY mapping file is optional - skip validation

    setIsUploading(true);
    try {
      const formData = new FormData();

      // Only append files if in upload mode
      if (!isFetchMode) {
        files.forEach(file => {
          formData.append('files', file);
        });
      }
      formData.append('subAction', subAction);

      // Add fetch from platform flag
      if (isFetchMode) {
        formData.append('fetchFromPlatform', 'true');
        console.log('🔄 Fetch from platform mode enabled');
      }

      // Extract platform IDs from orchestration status (if available)
      console.log('🔍 About to call extractPlatformIds(), orchestrationStatus:', orchestrationStatus);
      const platformIds = extractPlatformIds();
      console.log('🔍 extractPlatformIds() returned:', platformIds);
      if (platformIds) {
        // Send single campaign ID for backward compatibility
        formData.append('extractedCampaignId', platformIds.platformCampaignId);

        // Send multiple campaign IDs for Google Ads, Reddit, and TTD (avoid regressions for other platforms)
        if ((campaign.destination === 'google-ads-search' || campaign.destination === 'google-ads-pmax' || campaign.destination === 'google-ads-demandgen' || campaign.destination === 'reddit' || campaign.destination === 'ttd') && platformIds.platformCampaignIds && Array.isArray(platformIds.platformCampaignIds)) {
          formData.append('extractedCampaignIds', JSON.stringify(platformIds.platformCampaignIds));
        }

        if (platformIds.advertiserId) {
          formData.append('extractedAdvertiserId', platformIds.advertiserId);
        }
        if (platformIds.platformUrl) {
          formData.append('platformUrl', platformIds.platformUrl);
        }
        console.log('🎯 Sending extracted platform IDs:', platformIds);
      }

      // Add landing page URL if provided
      if (logoUrl.trim()) {
        formData.append('logoUrl', logoUrl.trim());
        console.log('🖼️ Sending logo URL:', logoUrl.trim());
      }

      if (landingPageUrl.trim()) {
        formData.append('landingPageUrl', landingPageUrl.trim());
        console.log('🔗 Sending landing page URL:', landingPageUrl.trim());
      }

      // Add MMGY mapping file if provided
      if (mmgyMappingFile) {
        formData.append('mmgyMappingFile', mmgyMappingFile);
        console.log('📋 Sending MMGY mapping file:', mmgyMappingFile.name);
      }

      // Add custom instruction if provided (MadHive / YouTube DV360)
      if (customInstruction.trim()) {
        formData.append('customInstruction', customInstruction.trim());
        console.log('📝 Sending custom instruction');
      }

      // Add YouTube DV360 ad naming + brief fields
      if (campaign.destination === 'youtube-dv360') {
        if (adPrefix.trim()) formData.append('adPrefix', adPrefix.trim());
        if (adSuffix.trim()) formData.append('adSuffix', adSuffix.trim());
        if (includeDate) formData.append('includeDate', 'true');
        if (mediaBrief.trim()) formData.append('mediaBrief', mediaBrief.trim());
      }

      // Dry-run (boolean) — DV360 + TTD share the same flag. Backend uploads
      // creatives, previews the mapping, and skips the assign step. User can
      // approve & apply from the dry-run review UI.
      if (campaign.destination === 'dv360' || campaign.destination === 'ttd') {
        if (dryRun) formData.append('dryRun', 'true');
      }
      // creative_name_filter is DV360-fetch-only.
      if (campaign.destination === 'dv360' && isFetchMode && creativeNameFilter.trim()) {
        formData.append('creativeNameFilter', creativeNameFilter.trim());
      }

      // Add page_id for Facebook campaigns if provided
      if (campaign.destination === 'facebook' && pageId.trim()) {
        formData.append('pageId', pageId.trim());
        console.log('📄 Sending page ID:', pageId.trim());
      }

      // Add instagram_user_id for Facebook campaigns if selected. Overrides
      // the IG account encoded in the creative text file on the launcher side.
      if (campaign.destination === 'facebook' && instagramUserId.trim()) {
        formData.append('instagramUserId', instagramUserId.trim());
        console.log('📷 Sending Instagram User ID:', instagramUserId.trim());
      }

      // Add ad name for Facebook campaigns if provided
      if (campaign.destination === 'facebook' && adName.trim()) {
        formData.append('adName', adName.trim());
        console.log('🏷️ Sending ad name:', adName.trim());
      }

      // Add target ad set IDs for Facebook campaigns if selected
      if (campaign.destination === 'facebook' && selectedAdSetIds.length > 0) {
        formData.append('targetAdSetIds', JSON.stringify(selectedAdSetIds));
        console.log('🎯 Sending target ad set IDs:', selectedAdSetIds);
      }

      const response = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/upload-creatives`),
        formData,
        {
          params: {
            accountId,
            userId: user?.userId || user?.id
          },
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      onUploadComplete(response.data);
      onClose();
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const getAcceptedFileTypes = () => {
    if (campaign?.destination === 'tiktok') return '.xlsx,.xlsm';
    if (subAction === 'hosted-native' || subAction === 'hosted-display') {
      return '.zip';
    }
    // For Reddit platform, accept ZIP files only
    if (campaign?.destination === 'reddit') {
      return '.zip';
    }
    // For Facebook platform, accept ZIP and TXT files (ad tags)
    if (campaign?.destination === 'facebook') {
      return '.zip,.txt';
    }
    // For TTD platform, accept TXT, Excel, and ZIP files (depending on sub-action)
    if (campaign?.destination === 'ttd') {
      return '.xlsx,.xls,.xlsm,.txt';
    }
    // For Madhive platform, accept Excel files (CTV) and TXT files (Display)
    if (campaign?.destination === 'madhive') {
      return '.xlsx,.xls,.xlsm,.txt';
    }
    // For OpenAI platform, accept ZIP (images + Excel ad copy) and JSON (ad configs)
    if (campaign?.destination === 'openai') {
      return '.zip,.json';
    }
    return '.xlsx,.xls,.xlsm';
  };

  const getFileTypeDescription = () => {
    if (campaign?.destination === 'tiktok') {
      return 'Excel asset form (.xlsx, .xlsm) referencing existing TikTok video material IDs. Use TT_USER and a linked identity ID. For multiple ad groups, include adgroup_id or a unique adgroup_name. New ads are paused; raw video upload is not supported.';
    }
    if (subAction === 'hosted-native' || subAction === 'hosted-display') {
      return 'ZIP files containing creative assets';
    }
    // For Reddit platform, accept ZIP files only
    if (campaign?.destination === 'reddit') {
      return 'ZIP files (.zip) containing creative assets';
    }
    // For Facebook platform, accept ZIP and TXT files
    if (campaign?.destination === 'facebook') {
      return 'ZIP (.zip) or TXT (.txt) files containing creative assets';
    }
    // For TTD platform, mention TXT and ZIP support
    if (campaign?.destination === 'ttd') {
      return 'Excel files (.xlsx, .xls, .xlsm), TXT files (.txt)';
    }
    // For Madhive platform, mention TXT support for Display
    if (campaign?.destination === 'madhive') {
      return 'Excel files (.xlsx, .xls, .xlsm) for CTV, TXT files (.txt) for Display';
    }
    // For OpenAI platform, mention ZIP (images + Excel) and JSON (ad configs)
    if (campaign?.destination === 'openai') {
      return 'ZIP (.zip) with images + Excel ad copy, or JSON (.json) ad config files';
    }
    return 'Excel files (.xlsx, .xls, .xlsm)';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Creative Assets</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          <div className="campaign-info">
            <strong>{campaign.name}</strong> - {campaign.destination?.toUpperCase()}
            {isOrchestrationInProgress && (
              <div className="orchestration-warning">
                <small style={{ color: '#f39c12', fontWeight: 'bold' }}>
                  ⚠️ Orchestration in progress - upload will be available when complete
                </small>
              </div>
            )}
          </div>

          {/* Creative Source Selection - only show when fetch from platform is enabled */}
          {isFetchFromPlatformEnabled() && (
            <div className="creative-source-selector" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Creative Source:</label>
              <div style={{ display: 'flex', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="creativeSource"
                    value="upload"
                    checked={creativeSource === 'upload'}
                    onChange={(e) => setCreativeSource(e.target.value)}
                    disabled={isUploading}
                    style={{ marginRight: '6px' }}
                  />
                  Upload file
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="creativeSource"
                    value="fetch"
                    checked={creativeSource === 'fetch'}
                    onChange={(e) => setCreativeSource(e.target.value)}
                    disabled={isUploading}
                    style={{ marginRight: '6px' }}
                  />
                  Fetch from {campaign.destination?.toUpperCase()}
                </label>
              </div>
              {creativeSource === 'fetch' && (
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Active creatives from the past 60 days will be fetched and assigned to line items.
                </small>
              )}
            </div>
          )}

          {/* Only show Creative Type selector for DV360 (the only platform with multiple creative types) */}
          {campaign.destination === 'dv360' && creativeSource === 'upload' && (
            <div className="sub-action-selector">
              <label>Creative Type:</label>
              <select
                value={subAction}
                onChange={(e) => setSubAction(e.target.value)}
                disabled={isUploading}
              >
                <option value="display-video">Display Video</option>
                <option value="hosted-native">Hosted Native</option>
              </select>
              <small>{getFileTypeDescription()}</small>
            </div>
          )}

          {/* For all other platforms, show file type description without dropdown */}
          {campaign.destination !== 'dv360' && creativeSource === 'upload' && (
            <div className="file-type-info">
              <small>{getFileTypeDescription()}</small>
            </div>
          )}

          {/* File drop zone - only show when upload mode is selected */}
          {creativeSource === 'upload' && (
            <div
              className={`file-drop-zone ${dragActive ? 'active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={faUpload} size="2x" />
              <p>Drag and drop files here or click to select</p>
              <small>Accepted: {getFileTypeDescription()}</small>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={getAcceptedFileTypes()}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* File list - only show when files are selected and in upload mode */}
          {creativeSource === 'upload' && files.length > 0 && (
            <div className="file-list">
              <h4>Selected Files:</h4>
              {files.map((file, index) => (
                <div key={index} className="file-item">
                  <FontAwesomeIcon icon={faFile} />
                  <span>{file.name}</span>
                  <span className="file-size">
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                  <button
                    className="remove-file"
                    onClick={() => removeFile(index)}
                    disabled={isUploading}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Logo URL Input — PMax only, where it replaces the Landing Page URL
              as the required field. The PMax creative workbook supplies the final
              URL itself, but its Logo rows are placeholders; without an explicit
              logo every asset group falls back to its smallest square image. */}
          {isPmaxCampaign && (
            <div className="logo-url-input" style={{ marginTop: '16px' }}>
              <label htmlFor="logo-url" style={{ fontWeight: 600 }}>
                Logo URL{' '}
                {isLogoUrlRequired
                  ? <span style={{ color: '#e74c3c' }}>*</span>
                  : <span style={{ color: '#888', fontWeight: 400, fontSize: '0.85em' }}>(optional)</span>}
              </label>
              <input
                type="url"
                id="logo-url"
                placeholder="https://tpc.googlesyndication.com/simgad/..."
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                disabled={isUploading}
                required={isLogoUrlRequired}
                className="form-control"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: logoUrl.trim()
                    ? '1px solid #27ae60'
                    : (isLogoUrlRequired ? '1px solid #e74c3c' : '1px solid #ccc'),
                  borderRadius: '4px',
                  fontSize: '14px',
                  marginTop: '8px'
                }}
              />
              <small
                style={{
                  color: logoUrl.trim() ? '#666' : (isLogoUrlRequired ? '#e74c3c' : '#888'),
                  marginTop: '4px',
                  display: 'block',
                  fontSize: '12px'
                }}
              >
                {logoUrl.trim()
                  ? 'Applied as the 1:1 logo for every PMax asset group'
                  : 'Required — enter the 1:1 logo URL for the PMax asset groups'}
              </small>
            </div>
          )}

          {/* Landing Page URL Input — required by default (every account today
              behaves this way); becomes optional only when account setting
              `optional-landing-page-url-assign-creative` is explicitly 'true'. */}
          {(creativeSource === 'upload' || isMMGYAccount) && (
            <div className="landing-page-input" style={{ marginTop: '16px' }}>
              <label htmlFor="landing-page-url" style={{ fontWeight: 600 }}>
                Landing Page URL{' '}
                {isLandingPageUrlRequired
                  ? <span style={{ color: '#e74c3c' }}>*</span>
                  : <span style={{ color: '#888', fontWeight: 400, fontSize: '0.85em' }}>(optional)</span>}
              </label>
              <input
                type="url"
                id="landing-page-url"
                placeholder="https://example.com/landing-page"
                value={landingPageUrl}
                onChange={(e) => setLandingPageUrl(e.target.value)}
                disabled={isUploading}
                required={isLandingPageUrlRequired}
                className="form-control"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: landingPageUrl.trim()
                    ? '1px solid #27ae60'
                    : (isLandingPageUrlRequired ? '1px solid #e74c3c' : '1px solid #ccc'),
                  borderRadius: '4px',
                  fontSize: '14px',
                  marginTop: '8px'
                }}
              />
              <small
                style={{
                  color: landingPageUrl.trim()
                    ? '#666'
                    : (isLandingPageUrlRequired ? '#e74c3c' : '#888'),
                  marginTop: '4px',
                  display: 'block',
                }}
              >
                {landingPageUrl.trim()
                  ? 'Landing page URL for the creative assets'
                  : (isLandingPageUrlRequired
                      ? 'Required — enter the landing page URL for the creative assets'
                      : 'Optional — leave blank to skip landing page URL for these creatives')}
              </small>
            </div>
          )}

          {/* Custom Instruction - show for MadHive and YouTube DV360 */}
          {(campaign?.destination === 'madhive' || campaign?.destination === 'youtube-dv360') && (
            <div className="landing-page-input" style={{ marginTop: '16px' }}>
              <label htmlFor="custom-instruction">Custom Instruction (optional)</label>
              <textarea
                id="custom-instruction"
                placeholder="e.g. Assign all CTV creatives to line items containing 'Prospecting'"
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                disabled={isUploading}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  marginTop: '8px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                Provide specific instructions for how creatives should be assigned to line items
              </small>
            </div>
          )}

          {/* Ad Naming Options - show for YouTube DV360 only */}
          {campaign?.destination === 'youtube-dv360' && (
            <div style={{ marginTop: '16px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
              <label style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px', display: 'block' }}>Ad Naming Options</label>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="ad-prefix" style={{ fontSize: '12px', color: '#555' }}>Prefix</label>
                  <input
                    type="text"
                    id="ad-prefix"
                    placeholder="e.g. April"
                    value={adPrefix}
                    onChange={(e) => setAdPrefix(e.target.value)}
                    disabled={isUploading}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', marginTop: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="ad-suffix" style={{ fontSize: '12px', color: '#555' }}>Suffix</label>
                  <input
                    type="text"
                    id="ad-suffix"
                    placeholder="e.g. Drop"
                    value={adSuffix}
                    onChange={(e) => setAdSuffix(e.target.value)}
                    disabled={isUploading}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', marginTop: '4px' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={includeDate}
                    onChange={(e) => setIncludeDate(e.target.checked)}
                    disabled={isUploading}
                  />
                  Include date in ad name
                </label>
              </div>
              <small style={{ color: '#666', marginTop: '6px', display: 'block' }}>
                Ad names will be constructed as: Prefix_CreativeName_Date_Suffix
              </small>
            </div>
          )}

          {/* Media Brief - show for YouTube DV360 only */}
          {campaign?.destination === 'youtube-dv360' && (
            <div className="landing-page-input" style={{ marginTop: '16px' }}>
              <label htmlFor="media-brief">Media Brief (optional)</label>
              <textarea
                id="media-brief"
                placeholder="Paste your media brief here to auto-generate headlines, long headlines, and descriptions for each ad..."
                value={mediaBrief}
                onChange={(e) => setMediaBrief(e.target.value)}
                disabled={isUploading}
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  marginTop: '8px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                AI will generate unique headline, long headline, and description for each ad based on this brief
              </small>
            </div>
          )}

          {/* DV360 Advanced Options — dry-run + creative name filter */}
          {campaign?.destination === 'dv360' && (
            <div style={{ marginTop: '16px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
              <label style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                DV360 Advanced Options
              </label>

              <div style={{ marginTop: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    disabled={isUploading}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    <strong>Dry-run mode</strong>
                    <small style={{ display: 'block', color: '#666', fontWeight: 'normal', marginTop: '2px' }}>
                      Determine assignments and preview them without applying to DV360. Use this to verify the mapping before running for real.
                    </small>
                  </span>
                </label>
              </div>

              {/* Creative name filter — only for fetch-from-platform mode */}
              {creativeSource === 'fetch' && isFetchFromPlatformEnabled() && (
                <div style={{ marginTop: '12px' }}>
                  <label htmlFor="creative-name-filter" style={{ fontSize: '12px', color: '#555', display: 'block' }}>
                    Creative Name Filter (optional)
                  </label>
                  <input
                    type="text"
                    id="creative-name-filter"
                    placeholder="e.g. Q2 2026"
                    value={creativeNameFilter}
                    onChange={(e) => setCreativeNameFilter(e.target.value)}
                    disabled={isUploading}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', marginTop: '4px' }}
                  />
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    When fetching creatives from DV360, only include those whose display name contains this substring (case-insensitive).
                  </small>
                </div>
              )}
            </div>
          )}

          {/* TTD Advanced Options — same dry-run checkbox as DV360 */}
          {campaign?.destination === 'ttd' && (
            <div style={{ marginTop: '16px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
              <label style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                TTD Advanced Options
              </label>

              <div style={{ marginTop: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    disabled={isUploading}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    <strong>Dry-run mode</strong>
                    <small style={{ display: 'block', color: '#666', fontWeight: 'normal', marginTop: '2px' }}>
                      Upload creatives to TTD and preview the planned ad group → creative mapping without running the assign step. Use this to verify the mapping before applying for real.
                    </small>
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* MMGY Mapping File Input - only show for MMGY accounts */}
          {isMMGYAccount && (
            <div className="landing-page-input" style={{ marginTop: '16px' }}>
              <label htmlFor="mmgy-mapping-file">MMGY Mapping File (optional)</label>
              <div
                style={{
                  border: mmgyMappingFile ? '1px solid #27ae60' : '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '12px',
                  marginTop: '8px',
                  textAlign: 'center',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  backgroundColor: mmgyMappingFile ? '#f0faf0' : '#fafafa',
                }}
                onClick={() => !isUploading && mmgyFileInputRef.current?.click()}
              >
                {mmgyMappingFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                      <FontAwesomeIcon icon={faFile} style={{ marginRight: '8px' }} />
                      {mmgyMappingFile.name} ({(mmgyMappingFile.size / 1024).toFixed(1)} KB)
                    </span>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMmgyMappingFile(null);
                        if (mmgyFileInputRef.current) mmgyFileInputRef.current.value = '';
                      }}
                      disabled={isUploading}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ) : (
                  <span style={{ color: '#999' }}>Click to select MMGY mapping file (.xlsx, .csv)</span>
                )}
              </div>
              <input
                ref={mmgyFileInputRef}
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setMmgyMappingFile(e.target.files[0]);
                  }
                }}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {/* Page ID + Instagram account selectors - only for Facebook campaigns */}
          {campaign.destination === 'facebook' && (
            <>
              <div className="landing-page-input" style={{ marginTop: '16px' }}>
                <label htmlFor="page-id" style={{ fontWeight: 600 }}>
                  Facebook Page <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                {/* Dropdown path: pages loaded AND user hasn't chosen manual entry */}
                {fbPagesLoaded && !pageIdManualEntry && fbPages.length > 0 && (
                  <>
                    <select
                      id="page-id"
                      value={pageId}
                      onChange={(e) => {
                        if (e.target.value === '__manual__') {
                          setPageIdManualEntry(true);
                          setPageId('');
                        } else {
                          setPageId(e.target.value);
                        }
                      }}
                      disabled={isUploading}
                      required
                      className="form-control"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: pageId.trim() ? '1px solid #27ae60' : '1px solid #e74c3c',
                        borderRadius: '4px',
                        fontSize: '14px',
                        marginTop: '8px'
                      }}
                    >
                      <option value="">— Select a Facebook Page —</option>
                      {fbPages.map(p => (
                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                      ))}
                      <option value="__manual__">Enter Page ID manually…</option>
                    </select>
                    <small style={{ color: pageId.trim() ? '#666' : '#e74c3c', marginTop: '4px', display: 'block' }}>
                      {pageId.trim()
                        ? 'Pages accessible via your connected Facebook channel. Overrides the value saved on the campaign.'
                        : 'Required — select the Facebook Page that owns the ad creatives'}
                    </small>
                  </>
                )}
                {/* Manual-entry / fallback path */}
                {(pageIdManualEntry || (fbPagesLoaded && fbPages.length === 0)) && (
                  <>
                    <input
                      type="text"
                      id="page-id"
                      placeholder="e.g. 123456789012345"
                      value={pageId}
                      onChange={(e) => setPageId(e.target.value)}
                      disabled={isUploading}
                      required
                      className="form-control"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: pageId.trim() ? '1px solid #27ae60' : '1px solid #e74c3c',
                        borderRadius: '4px',
                        fontSize: '14px',
                        marginTop: '8px'
                      }}
                    />
                    <small style={{ color: pageId.trim() ? '#666' : '#e74c3c', marginTop: '4px', display: 'block' }}>
                      {!pageId.trim()
                        ? 'Required — enter the Facebook Page ID that owns the ad creatives'
                        : fbPagesError
                          ? `Pages list unavailable (${fbPagesError}). Enter the Page ID manually.`
                          : 'The Facebook Page ID that owns the ad creatives. Overrides the value saved in the campaign.'}
                    </small>
                    {fbPagesLoaded && fbPages.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setPageIdManualEntry(false); setPageId(''); }}
                        disabled={isUploading}
                        style={{
                          marginTop: '4px',
                          background: 'none',
                          border: 'none',
                          color: '#1976d2',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '13px'
                        }}
                      >
                        ← Back to page list
                      </button>
                    )}
                  </>
                )}
                {/* Initial loading state — show the skeleton until the effect settles */}
                {!fbPagesLoaded && (
                  <div style={{ marginTop: '8px', color: '#999', fontSize: '14px' }}>
                    Loading Facebook pages…
                  </div>
                )}
              </div>

              {/* Instagram account selector — only after a page is selected */}
              {pageId && (
                <div className="landing-page-input" style={{ marginTop: '16px' }}>
                  <label htmlFor="ig-user-id">Instagram Account (optional)</label>
                  {igAccountsLoading && (
                    <div style={{ marginTop: '8px', color: '#999', fontSize: '14px' }}>
                      Loading connected Instagram accounts…
                    </div>
                  )}
                  {!igAccountsLoading && igAccountsError && (
                    <small style={{ color: '#b42318', marginTop: '8px', display: 'block' }}>
                      Couldn’t load Instagram accounts: {igAccountsError}
                    </small>
                  )}
                  {!igAccountsLoading && !igAccountsError && igAccounts.length === 0 && (
                    <small style={{ color: '#666', marginTop: '8px', display: 'block' }}>
                      No Instagram account is connected to this page. Reels/IG placements will be skipped.
                    </small>
                  )}
                  {!igAccountsLoading && igAccounts.length > 0 && (
                    <>
                      <select
                        id="ig-user-id"
                        value={instagramUserId}
                        onChange={(e) => setInstagramUserId(e.target.value)}
                        disabled={isUploading}
                        className="form-control"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          marginTop: '8px'
                        }}
                      >
                        {igAccounts.length > 1 && <option value="">— Select an Instagram Account —</option>}
                        {igAccounts.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.username ? `@${a.username}` : 'Instagram account'} ({a.id}{a.source === 'connected' ? ' — connected' : ''})
                          </option>
                        ))}
                      </select>
                      <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                        The Instagram Business account used for IG-placement ads. Overrides any IG ID in the uploaded file.
                      </small>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* Ad Name Input - only show for Facebook campaigns */}
          {campaign.destination === 'facebook' && (
            <div className="landing-page-input" style={{ marginTop: '16px' }}>
              <label htmlFor="ad-name">Ad Name (optional)</label>
              <input
                type="text"
                id="ad-name"
                placeholder="e.g. Summer Campaign - Static Ad"
                value={adName}
                onChange={(e) => setAdName(e.target.value)}
                disabled={isUploading}
                className="form-control"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  marginTop: '8px'
                }}
              />
              <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                Custom name for the created ad(s). If uploading multiple files, each ad will be numbered.
              </small>
            </div>
          )}

          {/* Ad Set Selection - only show for Facebook campaigns with available ad sets */}
          {campaign.destination === 'facebook' && availableAdSets.length > 0 && (
            <div style={{ marginTop: '16px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <label style={{ fontWeight: 600, marginBottom: '10px', display: 'block' }}>
                Target Ad Sets (optional)
                <span style={{ fontWeight: 400, color: '#6c757d', fontSize: '0.9em' }}> ({availableAdSets.length} available)</span>
              </label>
              <small style={{ color: '#666', marginBottom: '12px', display: 'block' }}>
                Select specific ad sets to assign creatives to. If none selected, AI will choose automatically.
              </small>

              <div style={{ marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #dee2e6' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 500, marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={adSetSelectAll}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAdSetSelectAll(checked);
                      setSelectedAdSetIds(checked ? availableAdSets.map(a => a.id) : []);
                    }}
                    disabled={isUploading}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>Select All</span>
                </label>
              </div>

              <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '5px 0' }}>
                {availableAdSets.map(adSet => (
                  <div key={adSet.id} style={{ padding: '8px 10px', marginBottom: '4px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0, fontWeight: 400 }}>
                      <input
                        type="checkbox"
                        checked={selectedAdSetIds.includes(adSet.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAdSetIds([...selectedAdSetIds, adSet.id]);
                          } else {
                            setSelectedAdSetIds(selectedAdSetIds.filter(id => id !== adSet.id));
                            setAdSetSelectAll(false);
                          }
                        }}
                        disabled={isUploading}
                        style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <span style={{ flex: 1, fontWeight: 500, color: '#212529' }}>{adSet.name}</span>
                      <span style={{ fontSize: '0.85em', color: '#6c757d', fontFamily: "'Courier New', monospace" }}>ID: {adSet.id}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={(creativeSource === 'upload' && files.length === 0) || isUploading || isOrchestrationInProgress || (isLandingPageUrlRequired && !landingPageUrl.trim()) || (isLogoUrlRequired && !logoUrl.trim()) || (campaign.destination === 'facebook' && !pageId.trim())}
            title={isOrchestrationInProgress ? 'Cannot upload while orchestration is in progress' : (isLandingPageUrlRequired && !landingPageUrl.trim()) ? 'Landing Page URL is required' : (isLogoUrlRequired && !logoUrl.trim()) ? 'Logo URL is required' : (campaign.destination === 'facebook' && !pageId.trim()) ? 'Facebook Page is required' : ''}
          >
            {isUploading ? (creativeSource === 'fetch' ? 'Processing...' : 'Uploading...') :
             isOrchestrationInProgress ? 'Orchestration in Progress...' :
             (creativeSource === 'fetch' ? 'Fetch & Assign Creatives' : 'Upload Creatives')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadCreativesModal;
